import { useCallback, useMemo, useState } from 'react';
import { enrichQuotesWithROI } from '@shared';

import { adjustQuotesForPremium } from '@client/util/quoteAdjustment';
import type { D2CPaymentTab } from '@shared/guidedPlanningPreferences';

import {
  type GuidedPlanningQuote,
  isTraditionalLTCQuote,
  getQuoteId,
  getPremiumAmount,
} from './use-quotes-display';
import type { PaymentTabData } from './use-quotes-by-payment-tab';
import {
  type PremiumBounds,
  computeTabPremiumBounds,
  snapPremiumToStep,
} from '@/features/guided-financial-planning/utils/premium-bounds-from-quotes';

export interface TabPremiumState {
  value: number;
  bounds: PremiumBounds;
}

export interface PerTabPremiumApi {
  getTabState: (tab: D2CPaymentTab) => TabPremiumState | null;
  setTabPremium: (tab: D2CPaymentTab, value: number) => void;
  getAdjustedQuotes: (tab: D2CPaymentTab) => GuidedPlanningQuote[];
}

interface UsePerTabPremiumStateOptions {
  tabs: PaymentTabData[];
  client: Client | undefined;
}

/**
 * The ROI metric used for enrichment and ranking of quotes. Always IRR for
 * consistency across payment tabs — advisors should compare lump-sum,
 * 10-pay, monthly, and annual quotes on the same metric. The QuoteCard's
 * primary/secondary display falls back to Total ROI when IRR isn't
 * calculable for a particular product, so there's no risk of a card
 * rendering with no return information.
 *
 * `tab` is retained as a parameter to make the per-tab call site
 * self-documenting and to leave room for a future per-tab override if needed.
 */
export function getRoiMetricForTab(_tab: D2CPaymentTab): ReturnOnInvestmentCalculationType {
  return 'internalRateOfReturn';
}

/**
 * Manages per-tab premium state for quote adjustment.
 * Only stores committed (debounced) values — the slider component
 * manages its own local drag state for instant visual feedback.
 */
export function usePerTabPremiumState({
  tabs,
  client,
}: UsePerTabPremiumStateOptions): PerTabPremiumApi {
  const boundsMap = useMemo(() => {
    const map = new Map<D2CPaymentTab, PremiumBounds>();
    for (const { tab, quotes } of tabs) {
      if (quotes.length === 0) continue;

      if (tab === 'monthly' || tab === 'annual') {
        // For recurring premium tabs, derive bounds from actual premium amounts
        // (not investmentMin/Max which represent lump-sum investment ranges)
        const premiums = quotes.map(q => getPremiumAmount(q)).filter(p => p > 0);
        if (premiums.length === 0) continue;
        const rawMin = Math.min(...premiums);
        const rawMax = Math.max(...premiums);
        if (rawMin === rawMax) continue;
        const step = rawMax <= 1000 ? 10 : rawMax <= 10000 ? 100 : 1000;
        const min = Math.max(0, Math.floor(rawMin / step) * step);
        const max = Math.ceil(rawMax / step) * step;
        if (max <= min) continue;
        map.set(tab, { min, max, step, defaultValue: max });
      } else {
        const bounds = computeTabPremiumBounds(quotes);
        if (bounds) {
          map.set(tab, bounds);
        }
      }
    }
    return map;
  }, [tabs]);

  // Committed values only — updated after debounce from the slider component
  const [committedValues, setCommittedValues] = useState<Map<D2CPaymentTab, number>>(new Map());

  const getTabState = useCallback(
    (tab: D2CPaymentTab): TabPremiumState | null => {
      const bounds = boundsMap.get(tab);
      if (!bounds) return null;

      const value = committedValues.get(tab) ?? bounds.defaultValue;
      return { value, bounds };
    },
    [boundsMap, committedValues],
  );

  const setTabPremium = useCallback(
    (tab: D2CPaymentTab, value: number) => {
      const bounds = boundsMap.get(tab);
      if (!bounds) return;

      const snapped = snapPremiumToStep(value, bounds.min, bounds.max, bounds.step);
      setCommittedValues(prev => {
        const next = new Map(prev);
        next.set(tab, snapped);
        return next;
      });
    },
    [boundsMap],
  );

  const tabQuotesLookup = useMemo(() => {
    const map = new Map<D2CPaymentTab, GuidedPlanningQuote[]>();
    for (const { tab, quotes } of tabs) {
      map.set(tab, quotes);
    }
    return map;
  }, [tabs]);

  const getAdjustedQuotes = useCallback(
    (tab: D2CPaymentTab): GuidedPlanningQuote[] => {
      const quotes = tabQuotesLookup.get(tab);
      if (!quotes || quotes.length === 0) return [];

      const bounds = boundsMap.get(tab);
      if (!bounds) return quotes;

      const targetPremium = committedValues.get(tab) ?? bounds.defaultValue;

      const adjustableQuotes = quotes.filter(q => !isTraditionalLTCQuote(q));
      const traditionalQuotes = quotes.filter(q => isTraditionalLTCQuote(q));

      let adjustedHybrid: GuidedPlanningQuote[] = adjustableQuotes;
      if (adjustableQuotes.length > 0) {
        const adjusted = adjustQuotesForPremium(
          adjustableQuotes as unknown as PolicyQuote[],
          targetPremium,
        ) as unknown as GuidedPlanningQuote[];
        const adjustedById = new Map(adjusted.map(q => [getQuoteId(q), q]));
        adjustedHybrid = adjustableQuotes.map(
          q => adjustedById.get(getQuoteId(q)) ?? q,
        );
      }

      const filteredTraditional = traditionalQuotes.filter(
        q => getPremiumAmount(q) <= targetPremium,
      );

      const combined = [...adjustedHybrid, ...filteredTraditional];

      if (!client) return combined;

      return enrichQuotesWithROI(
        combined as unknown as PolicyQuote[],
        client,
        getRoiMetricForTab(tab),
        { handleTraditionalLTC: true },
      ) as GuidedPlanningQuote[];
    },
    [tabQuotesLookup, boundsMap, committedValues, client],
  );

  return { getTabState, setTabPremium, getAdjustedQuotes };
}
